/** Builds selected-item performance, return and chronological journey evidence. */
import {
   Timeline,
   TimelineConnector,
   TimelineContent,
   TimelineDot,
   TimelineItem,
   TimelineSeparator
} from "@mui/lab";
import {Alert, Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {
   DashboardBrandItemTimelineQuery,
   useDashboardBrandItemTimelineQuery
} from "../../../Queries";
import {DateFormat} from "../../../utility/DateFormat";

type TimelineBrandItem = NonNullable<DashboardBrandItemTimelineQuery["BrandItem"]>;
type BrandItemTimelineEventKind = "origin" | "inventory" | "logistics" | "sale" | "return" | "discarded";

interface BrandItemReturnDetail {
   key: string;
   date?: Date | null;
   reasonCode: string;
}

export interface BrandItemTimelineEvent {
   key: string;
   kind: BrandItemTimelineEventKind;
   label: string;
   date: Date;
   detail?: string;
}

interface SequencedTimelineEvent extends BrandItemTimelineEvent {
   sequence: number;
}

/** Combines an order reference and shop name without inventing missing context. */
const orderEventDetail = (orderName?: string | null, shopName?: string | null): string | undefined => {
   const parts = [orderName ? `Order ${orderName}` : undefined, shopName || undefined].filter(Boolean);
   return parts.length ? parts.join(" · ") : undefined;
};

/** Converts persisted lifecycle fields into stable, oldest-first user-facing events. */
export const buildBrandItemTimeline = (item?: TimelineBrandItem | null): BrandItemTimelineEvent[] => {
   if (!item) {
      return [];
   }

   const events: SequencedTimelineEvent[] = [];
   let sequence = 0;
   const tradeIn = item.tradeInItem?.tradeIn;
   const tradeInDetail = tradeIn?.tradeInNr ? `Trade-in #${tradeIn.tradeInNr}` : undefined;

   if (tradeIn?.dateTime || item.tradeInItem?.createdAt) {
      events.push({
         key: `trade-in-${tradeIn?._id || item.tradeInItem?._id}`,
         kind: "origin",
         label: "Trade-in registered",
         date: (tradeIn?.dateTime || item.tradeInItem?.createdAt)!,
         detail: tradeInDetail,
         sequence: sequence++
      });
   }
   if (item.createdAt) {
      events.push({
         key: `inventory-${item._id}`,
         kind: "inventory",
         label: "Added to inventory",
         date: item.createdAt,
         sequence: sequence++
      });
   }
   if (tradeIn?.registeredTs) {
      events.push({
         key: `received-${tradeIn._id}`,
         kind: "inventory",
         label: "Received",
         date: tradeIn.registeredTs,
         detail: tradeInDetail,
         sequence: sequence++
      });
   }

   for (const service of item.vas || []) {
      if (!service?.completed || !service.completedDate) {
         continue;
      }
      events.push({
         key: `vas-${service.vasId}-${new Date(service.completedDate).toISOString()}`,
         kind: "logistics",
         label: service.vasName,
         date: service.completedDate,
         detail: service.workflowName || undefined,
         sequence: sequence++
      });
   }

   if (item.firstListedDate) {
      events.push({
         key: `listed-${item._id}`,
         kind: "inventory",
         label: "Listed for sale",
         date: item.firstListedDate,
         sequence: sequence++
      });
   }

   const seenOrderlines = new Set<string>();
   for (const shopInfo of item.shops || []) {
      for (const orderline of shopInfo?.stockItem?.orderlines || []) {
         if (!orderline || orderline.cancelled || seenOrderlines.has(orderline._id)) {
            continue;
         }
         seenOrderlines.add(orderline._id);
         const detail = orderEventDetail(orderline.order?.orderName, shopInfo?.shop?.name);
         if (orderline.order?.orderDate) {
            events.push({
               key: `sold-${orderline._id}`,
               kind: "sale",
               label: "Sold",
               date: orderline.order.orderDate,
               detail,
               sequence: sequence++
            });
         }
         if (orderline.returnedTs) {
            events.push({
               key: `returned-${orderline._id}`,
               kind: "return",
               label: "Returned",
               date: orderline.returnedTs,
               detail,
               sequence: sequence++
            });
         }
      }
   }

   if (!events.some(event => event.kind === "sale") && item.soldTs) {
      events.push({
         key: `sold-${item._id}`,
         kind: "sale",
         label: "Sold",
         date: item.soldTs,
         sequence: sequence++
      });
   }
   if (item.discarded) {
      events.push({
         key: `discarded-${item._id}`,
         kind: "discarded",
         label: "Discarded",
         date: item.discarded,
         sequence: sequence++
      });
   }

   events.sort((left, right) => (
      new Date(left.date).getTime() - new Date(right.date).getTime()
      || left.sequence - right.sequence
   ));

   let saleCount = 0;
   let returnCount = 0;
   return events.map(({sequence: _sequence, ...event}) => {
      if (event.kind === "sale") {
         event.label = saleCount++ === 0 ? "Sold" : "Sold again";
      }
      if (event.kind === "return") {
         event.label = returnCount++ === 0 ? "Returned" : "Returned again";
      }
      return event;
   });
};

/** Counts distinct, non-cancelled sales represented by the item's historic order lines. */
const countItemSales = (item?: TimelineBrandItem | null): number => {
   const seen = new Set<string>();
   for (const shopInfo of item?.shops || []) {
      for (const orderline of shopInfo?.stockItem?.orderlines || []) {
         if (orderline && !orderline.cancelled && orderline.order) {
            seen.add(orderline._id);
         }
      }
   }
   return Math.max(seen.size, item?.soldTs ? 1 : 0);
};

/** Collects each persisted return that belongs to one of the item's order lines. */
const itemReturns = (item?: TimelineBrandItem | null): BrandItemReturnDetail[] => {
   const returns: BrandItemReturnDetail[] = [];
   const seen = new Set<string>();
   for (const shopInfo of item?.shops || []) {
      for (const orderline of shopInfo?.stockItem?.orderlines || []) {
         if (!orderline || orderline.cancelled) {
            continue;
         }
         for (const returned of orderline.order?.returnLines || []) {
            if (!returned
               || seen.has(returned._id)
               || String(returned.orderLineId) !== String(orderline._id)) {
               continue;
            }
            seen.add(returned._id);
            returns.push({
               key: returned._id,
               date: returned.createdAt || orderline.returnedTs,
               reasonCode: returned.returnReason
            });
         }
      }
   }
   return returns.sort((left, right) => (
      new Date(left.date || 0).getTime() - new Date(right.date || 0).getTime()
   ));
};

/** Assigns restrained semantic colors to lifecycle milestones. */
const timelineDotColor = (kind: BrandItemTimelineEventKind): "primary" | "success" | "warning" | "error" | "grey" => {
   if (kind === "sale") return "success";
   if (kind === "return") return "warning";
   if (kind === "discarded") return "error";
   if (kind === "logistics") return "primary";
   return "grey";
};

/** Loads and displays selected-item metrics, returns and its complete dated journey. */
export const BrandItemTimeline = ({
   brandItemId,
   brandHouseId
}: {
   brandItemId: string;
   brandHouseId: string;
}) => {
   const {data, loading, error, refetch} = useDashboardBrandItemTimelineQuery({
      variables: {brandItemId, brandHouseId},
      fetchPolicy: "cache-first"
   });

   if (loading) {
      return <Stack direction="row" spacing={1.5} alignItems="center" sx={{py: 2}}>
         <CircularProgress size={20}/>
         <Typography variant="body2" color="text.secondary">Loading item journey…</Typography>
      </Stack>;
   }
   if (error) {
      return <Alert severity="warning" action={<Button color="inherit" onClick={() => void refetch()}>
         Retry
      </Button>}>
         The item journey could not be loaded.
      </Alert>;
   }

   const item = data?.BrandItem;
   const events = buildBrandItemTimeline(item);
   const sold = countItemSales(item);
   const returns = itemReturns(item);
   const reasonLabels = new Map<string, string>();
   for (const optionSet of data?.Options || []) {
      for (const value of optionSet.values || []) {
         if (value) {
            reasonLabels.set(value.value, value.label);
         }
      }
   }

   return <Stack spacing={3}>
      <Box
         component="section"
         aria-label="Item performance"
         sx={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1}}
      >
         {[
            {label: "Age", value: item?.daysForSale == null ? "—" : `${Math.round(item.daysForSale)}d`},
            {label: "Clicks", value: Math.round(item?.views || 0)},
            {label: "Sold", value: sold}
         ].map(metric => <Box key={metric.label} sx={{borderTop: 2, borderColor: "divider", pt: 1}}>
            <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
            <Typography variant="subtitle1">{metric.value}</Typography>
         </Box>)}
      </Box>

      {sold > 0 && returns.length > 0 && <Box component="section" aria-labelledby="item-returns-heading">
         <Typography id="item-returns-heading" variant="h6" gutterBottom>Returns</Typography>
         <Stack>
            {returns.map(returned => <Box
               key={returned.key}
               sx={{display: "grid", gridTemplateColumns: "minmax(130px, auto) 1fr", gap: 2, py: 1, borderTop: 1, borderColor: "divider"}}
            >
               <Typography variant="body2" color="text.secondary">
                  {returned.date ? DateFormat.toReadable(returned.date) : "Date unavailable"}
               </Typography>
               <Typography variant="body2">
                  {reasonLabels.get(returned.reasonCode) || "Other"}
               </Typography>
            </Box>)}
         </Stack>
      </Box>}

      <Box component="section" aria-labelledby="item-journey-heading">
         <Typography id="item-journey-heading" variant="h6" gutterBottom>Item journey</Typography>
         {!events.length
            ? <Typography variant="body2" color="text.secondary">No dated lifecycle events are available.</Typography>
            : <Timeline
               position="right"
               sx={{
                  p: 0,
                  m: 0,
                  "& .MuiTimelineItem-root:before": {display: "none"}
               }}
            >
               {events.map((event, index) => <TimelineItem key={event.key}>
                  <TimelineSeparator>
                     <TimelineDot variant="outlined" color={timelineDotColor(event.kind)}/>
                     {index < events.length - 1 && <TimelineConnector/>}
                  </TimelineSeparator>
                  <TimelineContent sx={{pt: 0, pb: 2.5, minWidth: 0}}>
                     <Typography variant="subtitle2">{event.label}</Typography>
                     <Typography
                        component="time"
                        dateTime={new Date(event.date).toISOString()}
                        variant="caption"
                        color="text.secondary"
                        display="block"
                     >
                        {DateFormat.toReadable(event.date)}
                     </Typography>
                     {event.detail && <Typography variant="body2" color="text.secondary">
                        {event.detail}
                     </Typography>}
                  </TimelineContent>
               </TimelineItem>)}
            </Timeline>}
      </Box>
   </Stack>;
};
